import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';

import { assignMetadata } from '../../utils/assign-metadata';
import {
  createTranscriptCorrectionsAction,
  deleteTranscriptCorrectionsAction,
  updateTranscriptCorrectionsAction,
} from './actions';
import { TRANSCRIPT_CORRECTIONS_SLICE_NAME } from './constants';
import type { TranscriptCorrection } from './types';

const transcriptCorrectionsAdapter =
  createEntityAdapter<TranscriptCorrection>();
const transcriptCorrectionsSelectors =
  transcriptCorrectionsAdapter.getSelectors();

const initialState = {
  transcriptCorrections: transcriptCorrectionsAdapter.getInitialState(),
};

const slice = createSlice({
  name: TRANSCRIPT_CORRECTIONS_SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateTranscriptCorrectionsAction, (state, action) => {
      const { data } = action.payload;
      data.corrections.forEach(correction => {
        const existingCorrection = transcriptCorrectionsSelectors.selectById(
          state.transcriptCorrections,
          correction.id,
        );

        let upsertCorrection: TranscriptCorrection;
        if (existingCorrection) {
          upsertCorrection = assignDefinedFields(
            { ...existingCorrection },
            correction,
          );
        } else {
          upsertCorrection = {
            ...correction,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
        }
        assignMetadata(action, upsertCorrection);

        transcriptCorrectionsAdapter.upsertOne(
          state.transcriptCorrections,
          upsertCorrection,
        );
      });
    });

    builder.addCase(createTranscriptCorrectionsAction, (state, action) => {
      const { id, name, description, regularExpressions } = action.payload;
      const transcriptCorrection: TranscriptCorrection = {
        id,
        name,
        description: description || '',
        regularExpressions: (regularExpressions || []).map(regex => ({
          ...regex,
          id: regex.id || uuidv4(),
        })),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, transcriptCorrection);
      transcriptCorrectionsAdapter.addOne(
        state.transcriptCorrections,
        transcriptCorrection,
      );
    });
    builder.addCase(deleteTranscriptCorrectionsAction, (state, action) => {
      const { transcriptCorrectionsId } = action.payload;
      transcriptCorrectionsAdapter.removeOne(
        state.transcriptCorrections,
        transcriptCorrectionsId,
      );
    });
  },
});

export { transcriptCorrectionsAdapter, transcriptCorrectionsSelectors };

export {
  createTranscriptCorrectionsAction,
  deleteTranscriptCorrectionsAction,
  updateTranscriptCorrectionsAction,
};
export default slice.reducer;
